/**
 * Classificação de erros Sentry: o que ignorar (ruído) vs. o que reportar com tags/fingerprints.
 */

export type SentryErrorCategory =
  | 'chunk_load'
  | 'schema_cache'
  | 'network'
  | 'rls'
  | 'service_worker'
  | 'auth_expected'
  | 'ai_quota'
  | 'unknown'

export type SentryErrorClassification =
  | { action: 'drop' }
  | { action: 'send'; category: SentryErrorCategory; fingerprint: string[] }

const CHUNK_LOAD_RE =
  /failed to fetch dynamically imported module|importing a module script failed|chunkloaderror|loading chunk \d+ failed/i

const SCHEMA_CACHE_RE = /pgrst204|schema cache|could not find the table/i

const RLS_RE = /row-level security|violates row-level security/i

const NETWORK_RE =
  /^(?:typeerror:\s*)?failed to fetch(?:\s*\(|$)|^load failed$|networkerror when attempting to fetch resource|network request failed/i

const SERVICE_WORKER_RE =
  /failed to update a serviceworker|failed to register a serviceworker|unknown error occurred when fetching the script/i

/** Padrões ignorados — apenas ruído genuíno ou fluxo já tratado na UI. */
const DROP_RULES: (string | RegExp)[] = [
  /^ResizeObserver loop/i,
  'ResizeObserver loop completed with undelivered notifications',
  'Non-Error promise rejection captured',
  /^AbortError/i,
  'The user aborted a request',
  'AuthRetryableFetchError',
  'AuthSessionMissingError',
  'Auth session missing',
  'Sessão expirada ou inválida',
  'Supabase ainda não foi inicializado',
  'A valid external Instance reference no longer exists',
  'Cache.add() encountered a network error',
  'O serviço de IA atingiu o limite de processamento',
  'WORKER_RESOURCE_LIMIT',
  /limite de processamento/i,
]

export const SENTRY_IGNORE_ERRORS: (string | RegExp)[] = DROP_RULES

export const primaryErrorMessage = (message: string, type?: string): string => {
  const m = message.trim()
  if (m) return m
  return type?.trim() ?? ''
}

const matchesDropRule = (message: string): boolean =>
  DROP_RULES.some((rule) => (typeof rule === 'string' ? message.includes(rule) : rule.test(message)))

export const classifySentryError = (message: string, type?: string): SentryErrorClassification => {
  const primary = primaryErrorMessage(message, type)
  if (!primary) return { action: 'send', category: 'unknown', fingerprint: ['synap-unknown'] }

  if (matchesDropRule(primary)) return { action: 'drop' }

  if (CHUNK_LOAD_RE.test(primary)) {
    return { action: 'send', category: 'chunk_load', fingerprint: ['synap-chunk-load'] }
  }
  if (SCHEMA_CACHE_RE.test(primary)) {
    return { action: 'send', category: 'schema_cache', fingerprint: ['synap-postgrest-schema-cache'] }
  }
  if (RLS_RE.test(primary)) {
    return { action: 'send', category: 'rls', fingerprint: ['synap-postgrest-rls'] }
  }
  if (SERVICE_WORKER_RE.test(primary)) {
    return { action: 'send', category: 'service_worker', fingerprint: ['synap-pwa-service-worker'] }
  }
  if (NETWORK_RE.test(primary)) {
    return { action: 'send', category: 'network', fingerprint: ['synap-network-fetch'] }
  }

  return { action: 'send', category: 'unknown', fingerprint: ['synap-error'] }
}
